/**
 * ModalTabsOverflowBar - Barra de abas de modais com overflow em botão "..."
 *
 * Renderiza as abas que cabem na largura disponível e move as excedentes
 * para um botão "..." (DropdownMenu), eliminando a barra de rolagem horizontal.
 * Se a aba ativa estiver no overflow, o botão "..." recebe o destaque de ativo.
 *
 * Medição: uma linha oculta renderiza todas as abas para medir larguras reais;
 * um ResizeObserver recalcula quantas cabem ao redimensionar o modal.
 *
 * @version 1.2.0
 * RELEASE: 2026-10-17 v1.2.0 - Larguras decimais, limite pela área visível do modal,
 *          folga de 8px e botão "..." fixo à direita (ml-auto).
 * RELEASE: 2026-10-17 v1.1.0 - Desconta a margem interna, recalcula ao mudar a janela
 *          ou a lista (chave estável), container com w-full/min-w-0; regra em tabsOverflow.
 * RELEASE: 2026-10-09 v1.0.0 - Criação inicial (modais de Veículos e Clientes)
 */

import { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { MoreHorizontal, type LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import { contarAbasQueCabem } from '@/lib/ui/tabsOverflow';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

/** Item de aba exibido na barra */
export interface ModalTabOverflowItem {
  id: string;
  label: string;
  icon: LucideIcon;
  /** Contador opcional exibido ao lado do rótulo (oculto quando 0/undefined) */
  count?: number;
}

interface ModalTabsOverflowBarProps {
  items: ModalTabOverflowItem[];
  activeId: string;
  onChange: (id: string) => void;
}

/** Largura estimada do botão "..." (px) reservada no cálculo de overflow */
const MORE_BUTTON_WIDTH = 44;

/** Contador compacto reutilizado na aba e no item do dropdown */
function TabCount({ count, isActive }: { count: number; isActive: boolean }) {
  return (
    <span
      className={cn(
        'text-[10px] min-w-[16px] h-4 px-1 rounded-full flex items-center justify-center',
        isActive ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground',
      )}
    >
      {count}
    </span>
  );
}

export function ModalTabsOverflowBar({ items, activeId, onChange }: ModalTabsOverflowBarProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLDivElement>(null);
  // Quantidade de abas visíveis fora do "..." (inicialmente todas, até medir)
  const [visibleCount, setVisibleCount] = useState(items.length);

  // v1.1.0: chave estável — evita recalcular a cada render (array recriado pelo pai)
  const chave = items.map((t) => `${t.id}:${t.label}:${t.count ?? ''}`).join('|');

  // Recalcula quantas abas cabem sempre que o tamanho do container ou a lista mudar
  useLayoutEffect(() => {
    const container = containerRef.current;
    const measure = measureRef.current;
    if (!container || !measure) return;

    const calculate = () => {
      // v1.1.0: desconta a margem interna (px-4) do espaço útil
      const estilo = getComputedStyle(container);
      const padL = parseFloat(estilo.paddingLeft || '0');
      const padR = parseFloat(estilo.paddingRight || '0');
      let available = container.getBoundingClientRect().width - padL - padR;
      // v1.2.0: limita ao trecho realmente visível dentro do ancestral que corta (overflow oculto)
      const rect = container.getBoundingClientRect();
      let el = container.parentElement;
      while (el) {
        const ov = getComputedStyle(el).overflowX;
        if (ov === 'hidden' || ov === 'clip' || ov === 'auto' || ov === 'scroll') {
          const r = el.getBoundingClientRect();
          const visivel = Math.min(rect.right, r.right) - Math.max(rect.left, r.left) - padL - padR;
          available = Math.min(available, visivel);
        }
        el = el.parentElement;
      }
      // v1.2.0: larguras decimais exatas (evita soma de arredondamentos)
      const widths = (Array.from(measure.children) as HTMLElement[]).map((c) => c.getBoundingClientRect().width);
      setVisibleCount(contarAbasQueCabem(widths, available, MORE_BUTTON_WIDTH, 8));
    };

    calculate();
    const observer = new ResizeObserver(calculate);
    observer.observe(container);
    observer.observe(measure);
    window.addEventListener('resize', calculate);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', calculate);
    };
  }, [chave]);

  const visibleItems = useMemo(() => items.slice(0, visibleCount), [items, visibleCount]);
  const overflowItems = useMemo(() => items.slice(visibleCount), [items, visibleCount]);
  const activeInOverflow = overflowItems.some((tab) => tab.id === activeId);

  return (
    <div ref={containerRef} className="relative flex w-full min-w-0 items-center h-9 px-4 border-b bg-muted/20 shrink-0 overflow-hidden">
      {/* Linha oculta de medição: renderiza todas as abas para obter larguras reais */}
      <div ref={measureRef} className="absolute invisible flex items-center h-9 pointer-events-none" aria-hidden>
        {items.map((tab) => {
          const Icon = tab.icon;
          return (
            <div key={tab.id} className="flex items-center gap-1.5 px-3 h-full text-xs font-medium">
              <Icon className="h-3.5 w-3.5" />
              <span>{tab.label}</span>
              {tab.count !== undefined && tab.count > 0 && <TabCount count={tab.count} isActive={false} />}
            </div>
          );
        })}
      </div>

      {/* Abas visíveis */}
      {visibleItems.map((tab) => {
        const Icon = tab.icon;
        const isActive = activeId === tab.id;
        return (
          <button
            key={tab.id}
            onClick={() => onChange(tab.id)}
            className={cn(
              'flex items-center gap-1.5 px-3 h-full text-xs font-medium transition-colors relative shrink-0',
              'hover:text-foreground',
              isActive ? 'text-primary' : 'text-muted-foreground',
            )}
          >
            <Icon className="h-3.5 w-3.5" />
            <span>{tab.label}</span>
            {tab.count !== undefined && tab.count > 0 && <TabCount count={tab.count} isActive={isActive} />}
            {isActive && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary rounded-t-full" />}
          </button>
        );
      })}

      {/* Botão "..." com as abas excedentes */}
      {overflowItems.length > 0 && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              aria-label="Mais abas"
              className={cn(
                // v1.2.0: ml-auto fixa o "..." no canto direito da barra
                'ml-auto flex items-center justify-center px-2 h-full text-xs font-medium transition-colors relative shrink-0',
                'hover:text-foreground',
                activeInOverflow ? 'text-primary' : 'text-muted-foreground',
              )}
            >
              <MoreHorizontal className="h-4 w-4" />
              {activeInOverflow && (
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary rounded-t-full" />
              )}
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="min-w-[180px]">
            {overflowItems.map((tab) => {
              const Icon = tab.icon;
              const isActive = activeId === tab.id;
              return (
                <DropdownMenuItem
                  key={tab.id}
                  onClick={() => onChange(tab.id)}
                  className={cn('flex items-center gap-2 text-xs', isActive && 'text-primary font-medium')}
                >
                  <Icon className="h-3.5 w-3.5" />
                  <span className="flex-1">{tab.label}</span>
                  {tab.count !== undefined && tab.count > 0 && <TabCount count={tab.count} isActive={isActive} />}
                </DropdownMenuItem>
              );
            })}
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </div>
  );
}
